import React, { useState, useRef, useContext, useEffect, useCallback, useMemo } from 'react';
import { Upload, Button, message } from 'antd';
import { UploadOutlined, CloseOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { UserContext } from '../../Contexts/UserContext';
import { PeFilterContext } from '../../Contexts/PeFilterContext';
import { filterCfsCompanies } from "../../Config/config";
import { useLocation } from 'react-router';
import { Input} from 'antd';


/**
 * MultiCompanySearch Component - CFS Specific
 * 
 * This component is optimized for CFS (Company Financial Search) pages only.
 * It allows users to:
 * - Search and add up to 10 companies with auto-suggestion (manual selection)
 * - Upload Excel files containing company names for bulk addition (unlimited, direct import)
 * - Manages company selection state for CFS filtering
 * 
 * CFS-specific optimizations:
 * - Uses dedicated `/refine/cfs/companies` API endpoint for single search validation
 * - Excel uploads directly import company names without API validation
 * - Single search payload: { search_key: "query" }
 * - Excel companies get unique IDs and are added directly to the list
 * - No complex access control or category handling
 */
const MultiCompanySearch = ({ onCompaniesChange }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [manuallySelectedCompanies, setManuallySelectedCompanies] = useState([]);
  const [excelUploadedCompanies, setExcelUploadedCompanies] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const searchInputRef = useRef(null);
  const dropdownRef = useRef(null);
  const { getToken } = useContext(UserContext);
  const { refineSearchFilters } = useContext(PeFilterContext);
  const location = useLocation();
  
  const maxManualCompanies = 10;

  useEffect(() => {
    // Initialize companies from CFS filters if they exist
    const savedCompanies = refineSearchFilters[location.pathname]?.multiCompanySearch || [];
    
    // Separate manually selected and excel uploaded companies
    const manualCompanies = savedCompanies.filter(company => company.source === 'manual' || !company.source);
    const excelCompanies = savedCompanies.filter(company => company.source === 'excel');
    
    setManuallySelectedCompanies(manualCompanies.slice(0, maxManualCompanies));
    setExcelUploadedCompanies(excelCompanies);
  }, [location.pathname, refineSearchFilters]);

  // Get all companies (manual + excel) for functionality
  const allCompanies = useMemo(() => {
    return [...manuallySelectedCompanies, ...excelUploadedCompanies];
  }, [manuallySelectedCompanies, excelUploadedCompanies]);

  // Notify parent whenever companies change
  useEffect(() => {
    if (onCompaniesChange && typeof onCompaniesChange === 'function') {
      onCompaniesChange(allCompanies);
    }
  }, [allCompanies, onCompaniesChange]);

  // Optimized for CFS - uses new CFS companies API with simplified payload
  const fetchCompanySuggestions = useCallback(async (query) => {
    try {
      const payload = {
        search_key: query
      };

      const response = await fetch(filterCfsCompanies, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': getToken()
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        if (data?.results) {
          // Filter out already selected companies (both manual and excel)
          const filteredSuggestions = data.results.filter(
            suggestion => !allCompanies.some(company => company.id === suggestion.id)
          );
          setSuggestions(filteredSuggestions);
          setShowSuggestions(true);
        }
      }
    } catch (error) {
      console.error('Error fetching company suggestions:', error);
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [getToken, allCompanies]);

  // Debounced search function
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchQuery.trim() !== "" && searchQuery.length >= 2) {
        fetchCompanySuggestions(searchQuery);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery, fetchCompanySuggestions]);

  // Handle clicks outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && 
          searchInputRef.current && !searchInputRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const addCompany = (company) => {
    if (manuallySelectedCompanies.length >= maxManualCompanies) {
      message.warning(`You can only manually add up to ${maxManualCompanies} companies`);
      return;
    }

    if (!allCompanies.some(c => c.id === company.id)) {
      const companyWithSource = { ...company, source: 'manual' };
      const updatedManualCompanies = [...manuallySelectedCompanies, companyWithSource];
      setManuallySelectedCompanies(updatedManualCompanies);
      setSearchQuery('');
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const removeManualCompany = (companyId) => {
    const updatedManualCompanies = manuallySelectedCompanies.filter(company => company.id !== companyId);
    setManuallySelectedCompanies(updatedManualCompanies);
  };

  const removeAllExcelCompanies = () => {
    setExcelUploadedCompanies([]);
  };

  const handleExcelUpload = (file) => {
    setIsUploading(true);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const workbook = XLSX.read(e.target.result, { type: 'binary' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const data = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        
        // Extract company names from the first column (skip header row)
        const companyNames = data.slice(1)
          .map(row => row[0])
          .filter(name => name && typeof name === 'string');

        if (companyNames.length === 0) {
          message.error('No valid company names found in the Excel file');
          setIsUploading(false);
          return;
        }

        // Process company names and add them directly
        processExcelCompanies(companyNames);
      } catch (error) {
        console.error('Error reading Excel file:', error);
        message.error('Error reading Excel file');
        setIsUploading(false);
      }
    };
    reader.readAsBinaryString(file);
    
    return false; // Prevent default upload
  };

  const processExcelCompanies = async (companyNames) => {
    try {
      if (companyNames.length === 0) {
        message.warning('No company names to process');
        setIsUploading(false);
        return;
      }

      // Create company objects directly from Excel data
      const newCompanies = companyNames
        .map((name, index) => ({
          id: `excel_${Date.now()}_${index}`, // Generate unique ID
          name: name.trim(),
          source: 'excel'
        }))
        .filter(company => company.name.length > 0)
        .filter(company => !allCompanies.some(c => c.name.toLowerCase() === company.name.toLowerCase()));

      if (newCompanies.length > 0) {
        const updatedExcelCompanies = [...excelUploadedCompanies, ...newCompanies];
        setExcelUploadedCompanies(updatedExcelCompanies);
        
        message.success(`Successfully added ${newCompanies.length} companies from Excel`);
      } else {
        message.warning('All companies from Excel are already in the list or have empty names');
      }
    } catch (error) {
      console.error('Error processing Excel companies:', error);
      message.error('Error processing companies from Excel file');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="space-y-4 w-full">
      {/* Search Input and Excel Upload Button */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center w-full gap-3 sm:gap-0">
        <div className="relative border border-gray-300 rounded-md p-1 w-full sm:w-[50%]">
          <Input
            ref={searchInputRef}
            type="text"
            style={{
              width: '100%',
              maxHeight: '60px',
              overflowY:"auto",
            }}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => {
              if (suggestions.length > 0) {
                setShowSuggestions(true);
              }
            }}
            placeholder="Search and add up to 10 companies"
            className="w-full px-4 py-2"
            bordered={false}
            disabled={manuallySelectedCompanies.length >= maxManualCompanies}
          />
          
          {/* Suggestions Dropdown */}
          {showSuggestions && suggestions.length > 0 && (
            <div 
              ref={dropdownRef}
              className="absolute z-50 w-full top-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-64 overflow-y-auto"
            >
              {suggestions.map((suggestion) => (
                <div
                  key={suggestion.id}
                  onClick={() => addCompany(suggestion)}
                  className="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                >
                  <div className="font-medium text-gray-800">{suggestion.name}</div>
                  {suggestion.industry && (
                    <div className="text-sm text-gray-500">{suggestion.industry}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-1 flex-shrink-0 mt-0 sm:mt-3 sm:ml-3 w-full sm:w-[45%]" style={{maxHeight:'auto'}}>
          <Upload
            accept=".xlsx,.xls"
            beforeUpload={handleExcelUpload}
            showUploadList={false}
            disabled={isUploading}
          >
            <Button
              style={{maxHeight:'40px'}}
              icon={<UploadOutlined />}
              loading={isUploading}
              className="border-[#BC8B20] text-[#BC8B20] hover:border-[#9A7419] hover:text-[#9A7419] text-xs sm:text-sm"
            >
              Upload Excel File
            </Button>
          </Upload>
          <span className="text-[10px] sm:text-xs text-gray-500 leading-tight">
            (For more than 10 companies, upload an excel with the company's registered name in the first column)
          </span>
        </div>
      </div>

      {/* Selected Companies Display */}
      <div className="space-y-2">
        {/* Manually Selected Companies */}
        {manuallySelectedCompanies.length > 0 && (
          <>
            <div className="text-sm font-medium text-gray-700">
              Selected Companies ({manuallySelectedCompanies.length}/{maxManualCompanies}):
            </div>
            <div className="flex flex-wrap gap-2">
              {manuallySelectedCompanies.map((company) => (
                <div
                  key={company.id}
                  className="flex items-center gap-2 bg-[#F1F5F9] px-3 py-1 rounded-full"
                >
                  <span className="text-[13px] font-medium">{company.name}</span>
                  <button
                    onClick={() => removeManualCompany(company.id)}
                    className="text-gray-500 hover:text-red-500 focus:outline-none"
                    aria-label={`Remove ${company.name}`}
                  >
                    <CloseOutlined className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Excel Uploaded Companies */}
        {excelUploadedCompanies.length > 0 && (
          <>
            <div className="text-sm font-medium text-gray-700">
              Excel Uploaded Companies:
            </div>
            <div className="flex items-center gap-2 bg-[#F1F5F9] px-3 py-2 rounded-md w-fit">
              <span className="text-[13px] font-medium">
                {excelUploadedCompanies.length} companies uploaded from Excel
              </span>
              <button
                onClick={removeAllExcelCompanies}
                className="text-gray-500 hover:text-red-500 focus:outline-none ml-auto"
                aria-label="Remove all Excel companies"
              >
                <CloseOutlined className="w-4 h-4" />
              </button>
            </div>
          </>
        )}
      </div>

    </div>
  );
};

export default MultiCompanySearch;